export function normalizeProjectName(name) {
  return String(name || "").trim();
}

export function projectNamesMatch(a, b) {
  const left = normalizeProjectName(a);
  const right = normalizeProjectName(b);
  if (!left || !right) return false;
  return left.toLowerCase() === right.toLowerCase();
}

export function isDeletedProjectAction(action) {
  return String(action || "").toUpperCase() === "DELETED";
}

export function deleteProjectConfirmCopy(name) {
  const label = normalizeProjectName(name) || "this project";
  return {
    title: "Delete Project?",
    body: `This is a permanent deletion. ${label} cannot be restored after it is deleted.`,
    detail:
      "The project and its related tasks and attachments may be deleted according to the backend deletion rules.",
    retain:
      "Excel Import History, users, notifications, reminders, time entries, and Documents data are retained.",
    confirmLabel: "Type the project name to confirm",
    confirmIntro:
      "To confirm permanent deletion, type the following project name:",
    confirmHint: "Enter the project name exactly as shown above.",
    confirmPlaceholder: "Enter project name",
  };
}

export function deleteProjectResultCopy(action) {
  if (String(action || "").toUpperCase() === "ARCHIVED") {
    return "";
  }
  return "Project deleted successfully.";
}

export function unexpectedDeleteActionCopy() {
  return "The project was not permanently deleted. No archive was performed from this action. You can try again if this was a temporary failure.";
}

export function deleteProjectErrorCopy(err) {
  const status = Number(err?.status);
  const code = String(err?.code || "").trim();
  let message = String(err?.message || "").trim();
  if (err?.isNetworkError) {
    message =
      "Unable to reach the server. Check your connection and try again.";
  } else if (!message) {
    if (status === 403) {
      message = "You do not have permission to delete this project.";
    } else if (status === 404) {
      message = "This project could not be found. It may have already been deleted.";
    } else if (status === 400) {
      message = "Project name confirmation does not match.";
    } else {
      message = "Unable to delete project.";
    }
  }
  if (code && !message.includes(code)) {
    message = `${message} (${code})`;
  }
  if (!/try again/i.test(message)) {
    message = `${message} You can try again if this was a temporary failure.`;
  }
  return message;
}

export function archiveProjectConfirmCopy(name) {
  const label = normalizeProjectName(name) || "this project";
  return {
    title: "Archive Project?",
    body: `Are you sure you want to archive ${label}?`,
    detail:
      "The project will no longer be available for new tasks, but existing tasks and history will be preserved.",
  };
}

export function restoreProjectConfirmCopy(name) {
  const label = normalizeProjectName(name) || "this project";
  return {
    title: "Restore Project?",
    body: `Restore ${label} and make it active again?`,
  };
}

export function emptyProjectsCopy(statusFilter) {
  const value = String(statusFilter || "ALL").toUpperCase();
  if (value === "ACTIVE") return "No active projects.";
  if (value === "ARCHIVED") return "No archived projects.";
  return "No projects found.";
}

export function projectStatusLabel(status) {
  return String(status || "ACTIVE").toUpperCase() === "ARCHIVED"
    ? "Archived"
    : "Active";
}

export function normalizeMemberEmail(value) {
  return String(value || "").trim().toLowerCase();
}

export function uniqueMemberEmails(values) {
  const seen = new Set();
  const emails = [];
  for (const value of values || []) {
    const raw =
      typeof value === "string" ? value : value?.email;
    const email = normalizeMemberEmail(raw);
    if (!email || seen.has(email)) continue;
    seen.add(email);
    emails.push(email);
  }
  return emails;
}

export const PROJECT_TYPES = ["INTERNAL", "EXTERNAL"];

export function normalizeProjectType(value) {
  const type = String(value || "").trim().toUpperCase();
  return PROJECT_TYPES.includes(type) ? type : "";
}

export function slugifyProjectName(name) {
  return String(name || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

export function generateProjectCode(type, name) {
  const normalizedType = normalizeProjectType(type);
  const slug = slugifyProjectName(name);
  if (!normalizedType || !slug) return "";
  const segment = normalizedType === "EXTERNAL" ? "EXT" : "INT";
  return `DGV-${segment}-${slug}`;
}

export function isProjectClassified(project) {
  return Boolean(
    normalizeProjectType(project?.projectType) &&
      String(project?.projectCode || "").trim()
  );
}

export function projectTypeLabel(type) {
  const normalized = normalizeProjectType(type);
  if (normalized === "INTERNAL") return "Internal";
  if (normalized === "EXTERNAL") return "External";
  return "Unclassified";
}

export function buildCreateProjectPayload({
  name,
  client,
  description,
  projectType,
  restricted,
  memberEmails,
} = {}) {
  const payload = {
    name: String(name || "").trim(),
    client: String(client || ""),
    description: String(description || ""),
    projectType: normalizeProjectType(projectType),
  };
  if (!restricted) return payload;
  return {
    ...payload,
    accessMode: "RESTRICTED",
    members: uniqueMemberEmails(memberEmails).map((email) => ({ email })),
  };
}

export function projectAccessModeLabel(accessMode) {
  return String(accessMode || "").trim().toUpperCase() === "RESTRICTED"
    ? "Restricted"
    : "Open";
}

export function isRestrictedAccessMode(accessMode) {
  return String(accessMode || "").trim().toUpperCase() === "RESTRICTED";
}

export function canShowManageAccess(project) {
  return (
    isRestrictedAccessMode(project?.accessMode) &&
    project?.canManageAccess === true
  );
}
